import { Link, useParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { news } from "@/mocks/news";

export default function NewsDetail() {
  const { id } = useParams();
  const article = news.find((item) => item.id === id);

  useDocumentMeta({
    title: article ? `${article.title} - 午夜场的电影资讯` : "资讯未找到 - 午夜场的电影",
    description: article
      ? article.summary
      : "未找到对应的午夜影视资讯，请返回资讯列表浏览其他内容。",
    keywords: article
      ? `${article.category},午夜电影资讯,影视资讯,${article.title}`
      : "午夜电影资讯",
  });

  if (!article) {
    return (
      <div className="mx-auto flex w-full max-w-[1280px] flex-col items-center justify-center px-4 py-32 text-center md:px-6 lg:px-10">
        <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-200/70 text-3xl text-foreground-500">
          <i className="ri-article-line"></i>
        </span>
        <h1 className="mb-2 font-heading text-2xl font-extrabold text-foreground-50">
          资讯不存在
        </h1>
        <p className="mb-6 text-sm text-foreground-500">
          这篇资讯可能已被移除或链接有误，去资讯列表看看其他内容吧。
        </p>
        <Link
          to="/news"
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = news.filter((item) => item.id !== article.id).slice(0, 4);

  return (
    <article className="mx-auto w-full max-w-[900px] px-4 py-10 md:px-6">
      <nav className="mb-6 flex flex-wrap items-center gap-2 text-xs text-foreground-600">
        <Link to="/" className="transition-colors hover:text-primary-400">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <Link to="/news" className="transition-colors hover:text-primary-400">
          影视资讯
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-300">{article.category}</span>
      </nav>

      <header className="mb-8">
        <span className="mb-3 inline-block rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-900">
          {article.category}
        </span>
        <h1 className="mb-4 font-heading text-2xl font-extrabold leading-snug tracking-tight text-foreground-50 md:text-4xl">
          {article.title}
        </h1>
        <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-600">
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {article.date}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-user-line"></i>
            {article.source}
          </span>
        </div>
      </header>

      <div className="mb-8 w-full overflow-hidden rounded-lg border border-background-200 bg-background-100">
        <img
          src={article.cover}
          alt={`${article.title} 配图 - 午夜电影资讯`}
          title={`${article.title} - 影视资讯`}
          className="h-auto w-full object-cover object-top"
        />
      </div>

      <div className="space-y-5">
        <p className="rounded-lg border-l-4 border-primary-500 bg-background-100 p-5 text-sm leading-loose text-foreground-300">
          {article.summary}
        </p>
        {article.content.map((paragraph) => (
          <p
            key={paragraph.slice(0, 16)}
            className="text-[15px] leading-loose text-foreground-400"
          >
            {paragraph}
          </p>
        ))}
      </div>

      <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-6">
        <Link
          to="/news"
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-200 transition-colors hover:border-primary-500 hover:text-primary-400"
        >
          <i className="ri-arrow-left-line"></i>
          返回资讯列表
        </Link>
        <span className="text-xs text-foreground-600">
          本站资讯为演示内容，正式上线请替换为真实稿件
        </span>
      </div>

      <section className="mt-12">
        <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-bold text-foreground-50">
          <i className="ri-file-list-3-line text-primary-400"></i>
          相关资讯
        </h2>
        <ul className="space-y-3">
          {related.map((item) => (
            <li key={item.id}>
              <Link
                to={`/news/${item.id}`}
                className="group flex items-center gap-4 rounded-lg border border-background-200/80 bg-background-100 p-4 transition-colors hover:border-primary-500/60"
              >
                <span className="rounded-md bg-background-200/70 px-2 py-1 text-[11px] font-semibold text-foreground-400">
                  {item.category}
                </span>
                <span className="flex-1 truncate text-sm font-medium text-foreground-100 transition-colors group-hover:text-primary-400">
                  {item.title}
                </span>
                <i className="ri-arrow-right-line shrink-0 text-foreground-600 transition-colors group-hover:text-primary-400"></i>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </article>
  );
}